import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Calendar, MapPin } from "lucide-react";
import api from "../lib/api";
import { resolveImg } from "../lib/img";
import { formatDate } from "../components/EventCard";
import SEO, { breadcrumbLD, SITE_URL } from "../components/SEO";

export default function EvenementDetail() {
    const { slug } = useParams();
    const [ev, setEv] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        setEv(null);
        setError("");
        api.get(`/events/${slug}`).then((r) => setEv(r.data)).catch(() => setError("Événement introuvable."));
    }, [slug]);

    if (error) return (
        <div className="pt-40 px-6 text-center pb-40">
            <h1 className="cp-serif text-5xl">{error}</h1>
            <Link to="/evenements" className="cp-btn-ghost mt-10 inline-flex">Retour à l'agenda</Link>
        </div>
    );
    if (!ev) return <div className="pt-40 text-center text-cp-gris">Chargement…</div>;

    const evLD = {
        "@context": "https://schema.org",
        "@type": "Event",
        "name": ev.title,
        "startDate": ev.start_date,
        ...(ev.end_date ? {"endDate": ev.end_date} : {}),
        "eventStatus": "https://schema.org/EventScheduled",
        "eventAttendanceMode": "https://schema.org/OfflineEventAttendanceMode",
        "description": (ev.description || "").slice(0, 320),
        "image": ev.image ? (ev.image.startsWith("http") ? ev.image : `${SITE_URL}${ev.image}`) : undefined,
        "location": {
            "@type": "Place",
            "name": ev.location_name || ev.city_slug,
            "address": {
                "@type": "PostalAddress",
                "streetAddress": ev.address || "",
                "addressLocality": ev.city_slug || "",
                "addressRegion": "Normandie / Picardie",
                "addressCountry": "FR",
            },
        },
        ...(ev.website ? {
            "offers": {
                "@type": "Offer",
                "url": ev.website,
                "availability": "https://schema.org/InStock",
                ...(ev.price ? {"price": String(ev.price).replace(/[^0-9.,]/g, '') || "0", "priceCurrency": "EUR"} : {}),
            },
        } : {}),
        "organizer": {
            "@type": "Organization",
            "name": "Côte Perdue (relais)",
            "url": SITE_URL,
        },
    };

    return (
        <div data-testid={`event-detail-${slug}`}>
            <SEO
                title={ev.title}
                description={(ev.description || "").slice(0, 200) || `${ev.title} — événement à ${ev.location_name || ev.city_slug}`}
                image={ev.image}
                path={`/evenements/${slug}`}
                type="article"
                keywords={[ev.title, ev.category, ev.city_slug, "événement", "Normandie", "Picardie", "agenda"]}
                structuredData={[
                    evLD,
                    breadcrumbLD([
                        { name: "Accueil", path: "/" },
                        { name: "Agenda", path: "/evenements" },
                        { name: ev.title, path: `/evenements/${slug}` },
                    ]),
                ]}
            />
            <section className="relative min-h-[70vh] overflow-hidden">
                <img src={resolveImg(ev.image)} alt={ev.title} className="absolute inset-0 w-full h-full object-cover cp-kenburns" />
                <div className="absolute inset-0 cp-hero-overlay" />
                <div className="relative z-10 flex flex-col justify-end min-h-[70vh] px-6 md:px-12 py-20 max-w-[1600px] mx-auto">
                    <Link to="/evenements" className="cp-kicker text-cp-ivoire/70 hover:text-cp-ivoire flex items-center gap-2 mb-6">
                        <ArrowLeft size={14} strokeWidth={1.5} /> Agenda
                    </Link>
                    <p className="cp-kicker text-cp-sable mb-4">{ev.category}</p>
                    <h1 className="cp-serif text-cp-ivoire text-3xl sm:text-4xl md:text-5xl lg:text-[56px] xl:text-[72px] leading-[1.05] max-w-4xl">
                        {ev.title}
                    </h1>
                </div>
            </section>

            <section className="px-6 md:px-12 max-w-[1400px] mx-auto py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
                <aside className="md:col-span-4 flex flex-col gap-6">
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Quand</p>
                        <p className="flex items-center gap-2 text-cp-noir text-lg"><Calendar size={16} strokeWidth={1.5} /> {formatDate(ev.start_date)}</p>
                        {ev.end_date && <p className="text-cp-gris text-sm">Jusqu'au {formatDate(ev.end_date)}</p>}
                    </div>
                    <div>
                        <p className="cp-kicker text-cp-foret mb-2">Où</p>
                        <p className="flex items-start gap-2 text-cp-noir"><MapPin size={16} strokeWidth={1.5} className="mt-1" /> {ev.location_name}</p>
                        {ev.address && <p className="text-cp-gris text-sm mt-1">{ev.address}</p>}
                    </div>
                    {ev.price && (
                        <div>
                            <p className="cp-kicker text-cp-foret mb-2">Tarif</p>
                            <p className="cp-serif text-2xl">{ev.price}</p>
                        </div>
                    )}
                    {ev.website && (
                        <a href={ev.website} target="_blank" rel="noreferrer" className="cp-btn-primary">
                            Réserver / Plus d'infos
                        </a>
                    )}
                </aside>
                <div className="md:col-span-8">
                    <p className="text-cp-noir text-xl md:text-2xl font-light leading-relaxed whitespace-pre-line">{ev.description}</p>
                </div>
            </section>
        </div>
    );
}
